import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { programApi } from "@bw/shared/api";
import { useProgramPreviewIdentity } from "@bw/shared/hooks";
import type { Schema } from "@bw/shared/types/generated";

const PAGE_SIZE = 25;

/**
 * The PUBLISHED programs at ONE facility (GET /programs?facility_id&status=published).
 *
 * Discovery is facility-first (mirrors booking) — a player always passes a
 * facility_id; there is no cross-facility feed. A player only ever sees
 * `status=published` programs (server-enforced + requested here). `programType`
 * optionally narrows to clinic / camp / group.
 */
export function useFacilityPrograms(
  facilityId: string | undefined,
  programType?: "group" | "private" | "camp" | "clinic" | "league" | "package",
) {
  const identity = useProgramPreviewIdentity();
  const client = useQueryClient();
  const scope = [
    "classes",
    "facility-programs",
    identity,
    facilityId ?? "",
    programType ?? null,
  ];
  const scopeToken = JSON.stringify(scope);
  const [navigation, setNavigation] = useState({
    scopeToken,
    offsets: [0],
    selected: 0,
  });
  const current =
    navigation.scopeToken === scopeToken
      ? navigation
      : { scopeToken, offsets: [0], selected: 0 };
  const offset = current.offsets[current.selected];
  const query = useQuery<Schema["PublicProgramListResponse"]>({
    queryKey: [...scope, PAGE_SIZE, offset],
    queryFn: async ({ signal }) => {
      const result = await programApi.listPublicPrograms({
        limit: PAGE_SIZE,
        offset,
        facility_id: facilityId as string,
        ...(programType ? { program_type: programType } : {}),
      });
      if (signal.aborted)
        throw new DOMException("Class discovery was superseded", "AbortError");
      return result;
    },
    enabled: Boolean(facilityId),
    retry: false,
  });
  useEffect(
    () => () => {
      void client.cancelQueries({ queryKey: scope });
      client.removeQueries({ queryKey: scope });
    },
    [client, scopeToken],
  );
  useEffect(() => {
    for (const entry of client.getQueryCache().findAll({ queryKey: scope })) {
      if (!current.offsets.includes(entry.queryKey.at(-1) as number)) {
        void client.cancelQueries({ queryKey: entry.queryKey, exact: true });
        client.removeQueries({ queryKey: entry.queryKey, exact: true });
      }
    }
  }, [client, scopeToken, offset]);
  const ready = query.isSuccess && query.fetchStatus === "idle";
  const data = ready ? query.data : undefined;
  const canNext = Boolean(data && offset + data.items.length < data.total);
  return {
    ...query,
    data,
    ready,
    canNext,
    canPrevious: current.selected > 0,
    isLoading:
      Boolean(facilityId) && (query.isPending || query.fetchStatus !== "idle"),
    next: () => {
      if (!ready || !canNext) return;
      const nextOffset = offset + PAGE_SIZE;
      setNavigation(
        current.offsets[current.selected + 1] === nextOffset
          ? { ...current, selected: current.selected + 1 }
          : { ...current, offsets: [offset, nextOffset], selected: 1 },
      );
    },
    previous: () => {
      if (ready && current.selected > 0)
        setNavigation({ ...current, selected: current.selected - 1 });
    },
    restart: () => {
      void client.cancelQueries({ queryKey: scope });
      client.removeQueries({ queryKey: scope });
      setNavigation({ scopeToken, offsets: [0], selected: 0 });
    },
  };
}
